import { Injectable, OnDestroy } from '@angular/core'
import { Compression } from 'common/utility/compression/compression'
import { Observable, Subject, combineLatest, filter, of, take, takeUntil } from 'rxjs'
import { AuthenticationService, AuthenticationState } from 'services/authentication/authentication.service'
import { QueryStore } from 'services/definition/stores/query'
import { QuerySettingsStore } from 'services/definition/stores/query-settings'
import { IndexDbService } from 'services/indexdb/index-db.service'
import { ConnectionState } from 'services/socket/classes/connection-state'
import { NotificationStatus } from 'services/socket/classes/notification-status'
import { ResponseMessageTypes } from 'services/socket/classes/response-message-type'
import { SocketMethod } from 'services/socket/classes/socket-method'
import { SocketService } from 'services/socket/socket.service'
import { TokenService } from 'services/token/token.service'
import { Logger } from 'src/app/common/utility/logger/logger'
import { IQueryItem, IQueryItemStorage, IQueryList, IQueryListItem, IQueryRequestResult, QueryRequestStatus } from '.'
import { Strings } from '../../common/utility/string/string'

@Injectable({
  providedIn: 'root'
})
export class QueryService implements OnDestroy {
  // #region Private Members
  private unsubscribe: Subject<void> = new Subject()
  private queryUpdateSubject: Subject<IQueryRequestResult> = new Subject()
  private static queryList: Array<IQueryList> | undefined
  private static queries: Array<IQueryItemStorage> | undefined
  private static pendingQuery: IQueryItemStorage | undefined
  private static pendingDeleteQueryId: number | undefined
  private static pendingTransactionId: string | undefined
  private unmatchedQueries: Array<any> = []
  private static queriesReady: boolean = false
  // #endregion 

  // #region Public Properties
  public get QueriesReady(): boolean {
    return QueryService.queriesReady
  }

  public get getFirstQuery(): IQueryItem | undefined {
    let returnValue: IQueryItem | undefined = undefined
    if (QueryService.queries?.length) {
      const first: IQueryItemStorage = QueryService.queries[0]
      returnValue = {
        id: first.id,
        name: first.name,
        version: first.version,
        settings: first.settings
          ? JSON.parse(<string>Compression.decompress(this.stringToIntArray(<any>first.settings)))
          : null
      }
    }
    return returnValue
  }

  public get listenForQueryUpdates(): Subject<IQueryRequestResult> {
    return this.queryUpdateSubject
  }
  // #endregion

  // #region Private Properties
  private static get Queries(): Array<IQueryItemStorage> {
    QueryService.queries = QueryService.queries ?? []
    return QueryService.queries
  }

  private static set Queries(value: Array<IQueryItemStorage>) {
    QueryService.queries = value
  }

  private static get hasQueries(): boolean {
    return !!QueryService.queries?.length
  }
  // #endregion

  // #region Public Methods
  public getQueryList(): Array<IQueryListItem> {
    if (QueryService.hasQueries) {
      return QueryService.Queries.map(item => {
        return structuredClone({ id: item.id, name: item.name, version: item.version })
      })
    }
    return []
  }

  public getQueryById(id: number): IQueryItem | undefined {
    if (!QueryService.hasQueries) return undefined

    const found = QueryService.Queries.find(Query => Query.id === id)

    return !found
      ? undefined
      : {
        id: found.id,
        name: found.name,
        version: found.version,
        settings: this.deserializeSettings(found.settings)
      }
  }

  public reloadQueries() {
    QueryService.Queries = []
    const request = this.socketService.createRequest(SocketMethod.GetQueryList, this.tokenService.AuthenticationId, {}, true)
    this.socketService.sendMessage(request)
  }

  // #region Query Maintenance
  public createQuery(target: IQueryItem): void {
    const zipped = Compression.compress(JSON.stringify(target.settings))
    const Query: IQueryItemStorage = {
      id: target.id,
      name: target.name,
      version: target.version,
      settings: zipped !== undefined ? zipped.toString() : null
    }

    const request = this.socketService.createRequest(SocketMethod.CreateQuery, this.tokenService.AuthenticationId, Query, true)
    this.socketService.sendMessage(request)
    QueryService.pendingQuery = Query
    QueryService.pendingTransactionId = request.transactionId ?? Strings.Empty
  }

  public saveQuery(target: IQueryItem): void {
    const zipped = Compression.compress(JSON.stringify(target.settings))
    const query: IQueryItemStorage = {
      id: target.id,
      name: target.name,
      version: target.version,
      settings: zipped !== undefined ? zipped.toString() : null
    }

    const request = this.socketService.createRequest(SocketMethod.SaveQuery, this.tokenService.AuthenticationId, query, true)
    this.socketService.sendMessage(request)

    QueryService.pendingQuery = query
    QueryService.pendingTransactionId = request.transactionId ?? Strings.Empty
  }

  public deleteQuery(target: IQueryItem): void {
    const payload = {
      id: target.id
    }
    const request = this.socketService.createRequest(SocketMethod.DeleteQuery, this.tokenService.AuthenticationId, payload, true)
    this.socketService.sendMessage(request)
    QueryService.pendingDeleteQueryId = target.id
    QueryService.pendingTransactionId = request.transactionId ?? Strings.Empty
  }
  // #endregion
  // #endregion

  constructor(
    private tokenService: TokenService,
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private indexDbService: IndexDbService
  ) {
    this.listenForQueryMessages()
    this.listenForAuthenticationState()
  }

  // #region Lifecycle Handlers
  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }
  // #endregion

  // #region Private helper methods

  private stringToIntArray(query: any): Uint8Array {
    return Uint8Array.from(query.replace('[', '').replace(']', '').split(','))
  }

  private processGetQueryList(message: any) {
    QueryService.queryList = []
    if (message?.data?.status == NotificationStatus.Success) {
      QueryService.queryList = JSON.parse(message.data.message)

      if (QueryService?.queryList?.length) {
        this.getStoredQueries()
          .subscribe(storedQueries => {
            this.unmatchedQueries = this.unmatched(storedQueries, QueryService.queryList ?? [])

            if (!this.unmatchedQueries || !this.unmatchedQueries.length) {
              Logger.debug('Query list is up-to-date in the cache. Not fetching query settings', QueryService.queryList)
              this.getStoredQuerySettings()
            } else {
              Logger.debug(`${this.unmatchedQueries.length} query settings are out of date or not in the cache. Adding`, this.unmatchedQueries)
              this.storeQueries(this.unmatchedQueries)
                .subscribe(() => {
                  Logger.debug('Queries added to the cache. Adding', this.unmatchedQueries)
                  this.requestQuerySettings(this.unmatchedQueries)
                })
            }
          })
      }
      else {
        QueryService.queries = []
        this.queriesReloaded()
      }

    }
    else {
      Logger.error("Failed to retrieve query list")
      QueryService.queries = undefined
      this.queriesReloaded(false)
    }
  }

  private getServerQueryById(id: number): void {
    const payload = {
      id: id
    }
    const request = this.socketService.createRequest(SocketMethod.GetQueryById, this.tokenService.AuthenticationId, payload, true)
    this.socketService.sendMessage(request)
  }

  private createQueryRequestResult(status: QueryRequestStatus, data?: any): IQueryRequestResult {
    const result: IQueryRequestResult = {
      status: status,
      data: data
    }
    return result
  }

  private unmatched(storedQueries: Array<any>, queryList: Array<any>): Array<any> {
    if (!storedQueries) return []

    return queryList.filter(query => !storedQueries.some(stored =>
      stored.id === query.id && stored.modified === query.modified
    ))
  }

  private queriesReloaded(success: boolean = true): void {
    QueryService.queriesReady = success
    this.queryResult(success ? QueryRequestStatus.Reloaded : QueryRequestStatus.Failed)
  }

  private queryResult(status: QueryRequestStatus, data?: any): void {
    this.queryUpdateSubject.next(this.createQueryRequestResult(status, data))
  }

  private requestQuerySettings(queries: Array<any>): void {
    queries?.length ? queries.forEach(query => this.getServerQueryById(query.id)) : null
  }

  private removeUnmatchedQuery(id: number): void {
    if (!this.unmatchedQueries?.length) return
    this.unmatchedQueries = this.unmatchedQueries.filter(source => source.id !== id)
    if (!this.unmatchedQueries.length) {
      this.getStoredQuerySettings()
    }
  }

  private listenForAuthenticationState(): void {
    combineLatest([this.authenticationService.listenForAuthenticationState, this.socketService.ConnectionState]).
      pipe(takeUntil(this.unsubscribe))
      .subscribe(([authState, connectState]) => {
        if (authState === AuthenticationState.Authenticated && connectState === ConnectionState.Connected) {
          this.reloadQueries()
        }
        if (authState === AuthenticationState.NotAuthenticated || connectState !== ConnectionState.Connected) {
          QueryService.queryList = undefined
        }
      })
  }

  private listenForQueryMessages(): void {
    this.socketService.Messages.pipe(takeUntil(this.unsubscribe),
      filter((message: any) => 'messageType' in message)).
      subscribe((message: any) => {
        switch (message.messageType) {
          case ResponseMessageTypes.GetQueryList:
            this.processGetQueryList(message)
            break
          case ResponseMessageTypes.GetQueryById:
            this.processGetQueryById(message)
            break
          case ResponseMessageTypes.CreateQuery:
            this.processCreateQuery(message)
            break
          case ResponseMessageTypes.DeleteQuery:
            this.processDeleteQuery(message)
            break
          case ResponseMessageTypes.SaveQuery:
            this.processSaveQuery(message)
            break
        }
      })
  }

  private deserializeSettings(settings: string | null | undefined): any {
    return settings
      ? JSON.parse(<string>Compression.decompress(this.stringToIntArray(<any>settings)))
      : null
  }

  private removeQuery(id: number | undefined): void {
    if (!QueryService.hasQueries) return
    QueryService.Queries = QueryService.Queries.filter(query => query.id !== id)
  }

  private replaceQuery(updatedQuery: IQueryItemStorage): void {
    const index = QueryService.Queries.findIndex(query => query.id == QueryService.pendingQuery?.id)
    if (index > -1) QueryService.Queries[index] = updatedQuery
  }

  private clearPending(): void {
    QueryService.pendingQuery = QueryService.pendingTransactionId = QueryService.pendingDeleteQueryId = undefined
  }

  private getQueryId(message: any): number {
    const responseMessage = this.getResponseMessage(message)
    return responseMessage?.length ? responseMessage[0]['queryId'] : 0
  }

  private validCreateMessage(message: any): boolean {
    return this.validTransaction(message) && this.hasQueryId(message)
  }

  private validDeleteMessage(message: any): boolean {
    return !!QueryService.pendingDeleteQueryId && this.validTransaction(message)
  }

  private validTransaction(message: any): boolean {
    return this.successMessage(message) && this.pendingTransaction(message)
  }

  private successMessage(message: any): boolean {
    return message?.data?.status == NotificationStatus.Success
  }

  private pendingTransaction(message: any): boolean {
    return QueryService.pendingTransactionId === message?.transactionId
  }

  private hasQueryId(message: any): boolean {
    const responseMessage = this.getResponseMessage(message)
    return responseMessage?.length && responseMessage[0]['queryId']
  }

  private getResponseMessage(message: any): any {
    return JSON.parse(message?.data?.message)
  }

  // #region IndexedDb methods
  private storeQuerySettings(settings: IQueryItemStorage): Observable<any> {
    if (!settings) return of(null)

    Logger.debug(`Storing Query Settings: for query '${settings.name}'`)
    return this.indexDbService.add(QuerySettingsStore, settings).pipe(take(1))
  }

  private storeQueries(queries: any): Observable<any> {
    return this.indexDbService.addAll(QueryStore, queries).pipe(take(1))
  }

  private getStoredQueries(): Observable<any> {
    return this.indexDbService.all(QueryStore)
      .pipe(take(1))
  }

  private getStoredQuerySettings() {
    return this.indexDbService.all(QuerySettingsStore)
      .pipe(take(1))
      .subscribe((settings: Array<IQueryItemStorage>) => {
        QueryService.Queries = settings
        Logger.debug(`Loaded ${settings.length} queries`, this.unmatchedQueries)
        this.queriesReloaded()
      })
  }
  // #endregion

  // #endregion

  // #region Socket Response handlers
  private processCreateQuery(message: any): void {
    let status: QueryRequestStatus = QueryRequestStatus.Failed
    let data: any

    if (QueryService.pendingQuery && this.validCreateMessage(message)) {
      QueryService.pendingQuery.id = this.getQueryId(message)
      QueryService.Queries.push(QueryService.pendingQuery)

      status = QueryRequestStatus.Success
      data = QueryService.pendingQuery.id
    }
    this.clearPending()
    this.queryResult(status, data)
  }

  private processDeleteQuery(message: any) {
    const status: QueryRequestStatus = QueryService.hasQueries && this.validDeleteMessage(message)
      ? QueryRequestStatus.Success
      : QueryRequestStatus.Failed

    if (status === QueryRequestStatus.Success) {
      this.removeQuery(QueryService.pendingDeleteQueryId)
      this.clearPending()
    }
    this.queryResult(status)
  }

  private processSaveQuery(message: any) {
    const status: QueryRequestStatus = QueryService.hasQueries && QueryService.pendingQuery && this.validTransaction(message)
      ? QueryRequestStatus.Success
      : QueryRequestStatus.Failed

    if (status === QueryRequestStatus.Success) {
      this.replaceQuery(QueryService.pendingQuery!)
      this.clearPending()
    }
    this.queryResult(status)
  }

  private processGetQueryById(message: any) {
    if (message?.data?.status == NotificationStatus.Success) {
      const query: IQueryItemStorage = JSON.parse(message.data.message)
      this.storeQuerySettings(query).subscribe(() => {
        Logger.debug(`Stored query settings for '${query.name}'`)
        this.removeUnmatchedQuery(query.id)
      })
    }
  }
  // #endregion
}
